import { PALETTE } from './tokens';

/**
 * A colour profile as data: the six roles a tenant may set, and everything the
 * palette derives from them.
 *
 * A profile's colours are sparse. A role it does not set is Meridian's, with
 * Meridian's own hand-picked variants, so the empty profile is Meridian exactly
 * and an old version reads correctly after a role is added. A role it does set
 * has its variants, its text colour and its tints derived here. The API checks
 * the same contrast arithmetic on save (Api/services/colour_profile.py); the two
 * share their test vectors, so change them together.
 */

export const COLOUR_ROLES = ['primary', 'secondary', 'success', 'warning', 'error', 'info'] as const;
export type ColourRole = (typeof COLOUR_ROLES)[number];
export type ProfileColours = Partial<Record<ColourRole, string>>;

export interface RoleColours {
  readonly main: string;
  readonly light?: string;
  readonly dark?: string;
  readonly contrastText: string;
}

type Widen<T> = T extends string
  ? string
  : T extends readonly string[]
    ? readonly string[]
    : { readonly [K in keyof T]: Widen<T[K]> };

export type Palette = Omit<Widen<typeof PALETTE>, ColourRole> & {
  readonly [R in ColourRole]: RoleColours;
} & { readonly primary: Required<RoleColours> };

export const MINIMUM_CONTRAST = 4.5;
const WHITE = '#FFFFFF';
const INK = PALETTE.text.primary;
const PAPER = PALETTE.background.paper;
const PAGE_TINT = 0.105;
const HEADER_TINT = 0.06;
const TOOLBAR_TINT = 0.15;
const HEX = /^#[0-9A-Fa-f]{6}$/;

const channels = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const toHex = (rgb: readonly number[]): string =>
  `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/** WCAG 2 relative luminance. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** White when it reads at AA, else the ink, else whichever reads better. */
export function onColour(main: string): string {
  const white = contrastRatio(WHITE, main);
  if (white >= MINIMUM_CONTRAST) return WHITE;
  const ink = contrastRatio(INK, main);
  if (ink >= MINIMUM_CONTRAST) return INK;
  return white >= ink ? WHITE : INK;
}

/** `amount` of the way towards white. */
export const lighten = (hex: string, amount: number): string =>
  toHex(channels(hex).map((c) => c + (255 - c) * amount));

/** `amount` of the way towards black. */
export const darken = (hex: string, amount: number): string =>
  toHex(channels(hex).map((c) => c * (1 - amount)));

/** `share` of the colour in white: Meridian's page background is its primary at 10.5%. */
export const tint = (hex: string, share: number): string =>
  toHex(channels(hex).map((c) => 255 + (c - 255) * share));

export const pageBackground = (primary: string): string => tint(primary, PAGE_TINT);

export function deriveRole(main: string): Required<RoleColours> {
  return { main, light: lighten(main, 0.15), dark: darken(main, 0.2), contrastText: onColour(main) };
}

/** A colour as text on `background`: itself when it reads at AA, else its darker variant, else the ink. */
export function readableShade(main: string, background: string = PAPER): string {
  if (contrastRatio(main, background) >= MINIMUM_CONTRAST) return main;
  const { dark } = deriveRole(main);
  return contrastRatio(dark, background) >= MINIMUM_CONTRAST ? dark : INK;
}

/** A role's colour as text on `background`: its dark shade when that reads at AA, else a deeper one of the same hue, else the ink. */
export function readableRoleText(role: RoleColours, background: string): string {
  const shades = [role.dark ?? deriveRole(role.main).dark, darken(role.main, 0.4), darken(role.main, 0.6)];
  return shades.find((shade) => contrastRatio(shade, background) >= MINIMUM_CONTRAST) ?? INK;
}

/**
 * The read side of a stored profile: unknown keys and malformed values are
 * dropped, and a role equal to Meridian's own is treated as unset, so it keeps
 * Meridian's variants rather than derived ones.
 */
export function readColours(raw: unknown): ProfileColours {
  if (typeof raw !== 'object' || raw === null) return {};
  const source = raw as Record<string, unknown>;
  const colours: ProfileColours = {};
  for (const role of COLOUR_ROLES) {
    const value = source[role];
    if (typeof value === 'string' && HEX.test(value) && value.toUpperCase() !== PALETTE[role].main.toUpperCase()) {
      colours[role] = value.toUpperCase();
    }
  }
  return colours;
}

/** Every role filled: the profile's where it sets one, Meridian's elsewhere. */
export function filledColours(colours: ProfileColours): Record<ColourRole, string> {
  const set = readColours(colours);
  return Object.fromEntries(COLOUR_ROLES.map((role) => [role, set[role] ?? PALETTE[role].main])) as Record<
    ColourRole,
    string
  >;
}

export function paletteFor(colours: ProfileColours): Palette {
  const set = readColours(colours);
  if (Object.keys(set).length === 0) return PALETTE;

  const role = (name: ColourRole): RoleColours => {
    const main = set[name];
    return main ? deriveRole(main) : PALETTE[name];
  };
  const primary = set.primary ? deriveRole(set.primary) : PALETTE.primary;
  const secondary = role('secondary');

  return {
    ...PALETTE,
    primary,
    secondary,
    success: role('success'),
    warning: role('warning'),
    error: role('error'),
    info: role('info'),
    button: {
      primary: set.primary ? { main: primary.main, contrastText: primary.contrastText } : PALETTE.button.primary,
      secondary: set.secondary
        ? { main: secondary.main, contrastText: secondary.contrastText }
        : PALETTE.button.secondary,
    },
    link: set.primary ? primary.main : PALETTE.link,
    background: set.primary ? { ...PALETTE.background, default: pageBackground(primary.main) } : PALETTE.background,
    surface: set.primary
      ? { ...PALETTE.surface, header: tint(primary.main, HEADER_TINT), toolbar: tint(primary.main, TOOLBAR_TINT) }
      : PALETTE.surface,
    gradient: set.primary
      ? { dark: darken(primary.main, 0.55), blue: primary.main, tealDeep: darken(primary.main, 0.33) }
      : PALETTE.gradient,
  };
}

export type ContrastPairing = 'text-on-colour' | 'link-on-paper' | 'link-on-page';

export interface ContrastFailure {
  role: ColourRole;
  pairing: ContrastPairing;
  ratio: number;
  required: number;
}

const rounded = (ratio: number) => Math.round(ratio * 100) / 100;

/**
 * The pairings a profile must carry at AA: text on every role it sets, and the
 * primary as link text on the paper and on the page background it tints.
 * Meridian sets nothing, so it is never refused for its own warning colour.
 */
export function contrastFailures(colours: ProfileColours): ContrastFailure[] {
  const set = readColours(colours);
  const failures: ContrastFailure[] = [];
  for (const role of COLOUR_ROLES) {
    const main = set[role];
    if (!main) continue;
    const ratio = contrastRatio(onColour(main), main);
    if (ratio < MINIMUM_CONTRAST) {
      failures.push({ role, pairing: 'text-on-colour', ratio: rounded(ratio), required: MINIMUM_CONTRAST });
    }
  }
  if (set.primary) {
    const onPaper = contrastRatio(set.primary, PAPER);
    if (onPaper < MINIMUM_CONTRAST) {
      failures.push({ role: 'primary', pairing: 'link-on-paper', ratio: rounded(onPaper), required: MINIMUM_CONTRAST });
    }
    const onPage = contrastRatio(set.primary, pageBackground(set.primary));
    if (onPage < MINIMUM_CONTRAST) {
      failures.push({ role: 'primary', pairing: 'link-on-page', ratio: rounded(onPage), required: MINIMUM_CONTRAST });
    }
  }
  return failures;
}
